import type { RefCallbackWithCleanup } from 'react';

/**
 * A factory function which creates a ref callback that will invoke the provided
 * callback when a click is detected outside of the attached element.
 * @param callback - The callback to call when a click outside of the element is detected.
 * @returns A ref callback that can be attached to a React element.
 * @example
 * const closeOnOutsideClick = useMemo(
 *  () => onClickedOutside<HTMLDivElement>(() => isOpen && onToggle()),
 *  [isOpen, onToggle]
 * );
 *
 * return <div ref={closeOnOutsideClick}>...</div>;
 *
 */
export function createClickOutsideRef<T extends Node>(
  callback: () => void
): RefCallbackWithCleanup<T> {
  return (node: T) => {
    const clickHandler = (evt: MouseEvent): void => {
      if (!node.contains(evt.target as Node)) callback();
    };

    /**
     * `document.documentElement` is used instead of `window` to ensure that the event
     * handler runs before React attempts to synchronously re-render, which it will do so
     * after "bubbling" the event to the "root", which in the case of Next.js, is the `document`.
     */
    document.documentElement.addEventListener('click', clickHandler);
    return () => {
      document.documentElement.removeEventListener('click', clickHandler);
    };
  };
}
